/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isObject from 'lodash.isobject';
import uniq from 'lodash.uniq';
import difference from 'lodash/difference';
import range from 'lodash/range';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {deletePegEvents} from '@ion/core/src/services/Connect';
import {
  AccumFieldsName,
  PegAccumulator,
  PegAccumulators,
  PegBehavior,
  PegEvent,
  PegEvents,
  PegLabels,
  PegLabelType,
  systemOfUnits
} from '@ion/core/src/types/entities';
import {
  safeJSONParse,
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  undefinedAndNullToString
} from '@ion/core/src/utils/StringUtil';
import {normalizePegAccumulator} from '@/store/actions/admin-pegbehaviors';
import {path} from './PegBehaviorsEditorUtils';

/**
 * Max Event and Accumulator Code Range.
 */
export const MAX_EVENT_CODE = 256;

//PEG Event and Accumulator List Id and Import Id.
export const ACCUMULATOR_LIST_ID   = 'accumulator-list';
export const ACCUMULATOR_IMPORT_ID = 'pegAccumulator-importer';
export const EVENT_LIST_ID         = 'event-list';
export const EVENT_IMPORT_ID       = 'pegEvent-importer';

/**
 * File types to support import peg event and accumulators.
 */
export const importFileTypes = '.csv, .xls, .xlsx';

/**
 * defines Add Event/Accumulators Error param types ...
 */
export type IsErrorMsgParams = {
  [key: string]: boolean;
};

/**
 * defines getUpdatePegAccumulator method param types ...
 */
export interface getUpdatePegAccumulatorParams extends PegAccumulator {
  fieldName?: AccumFieldsName;
};

/**
 * Get Default Event and Accumulator Code.
 */
export const getDefaultCode = (maxRange = MAX_EVENT_CODE): number[] => {
  return range(0, maxRange);
};

/**
 * Get PEG Label based on the type.
 */
export const getPEGLabels = (pegLabels: PegLabels, TYPE: String) => {
  return pegLabels?.filter(item => item.type === TYPE);
};

/**
 * Get the difference between available and default event codes / accumulator indexes.
 */
export const getDifference = (defArray: Array<number>, reqArray: Array<number>): number[] => {
  return difference(defArray, reqArray);
};

/**
 * Get the available event code
 */
export const getAvailableEventCode = (pegEvents: PegEvents) => {
  return pegEvents?.map(({pegEvent}) => pegEvent.eventCode);
};

/**
 * Check the duplication with the available peg events.
 */
export const isDuplicatePEGEvent = (pegEvents): boolean => {
  const unique = Array.from(new Set(
    pegEvents?.map((a: { pegEvent: { eventCode: number; }; }) => a.pegEvent.eventCode))
  );
  return unique.length !== pegEvents.length;
};

/**
 * Delete PEG Events by passing pegevent id and pegbehavior id as reference
 */
export const deletePegEvent = async(pegBehavior: PegBehavior, pegEvent: PegEvent) => {
  try {
    const results = await deletePegEvents(pegBehavior, pegEvent);
    return results;
  } catch (err) {
    error('Error while deleting the PEG Event', err);
    return err;
  }
};

/**
 * Delete Multiple PEG Events by passing pegevent id and pegbehavior id as reference
 */
export const deletePEGEvents = async(pegBehavior: PegBehavior, pegEvents: PegEvents) => {
  try {
    await Promise.all(pegEvents?.map(async({pegEvent}) => {
      if (pegEvent?.id) {
        await deletePegEvents(pegBehavior, pegEvent);
      }
    }));
  } catch (err) {
    error('Error while deleting the PEG Events', err);
    return err;
  }
};

/**
 * Get the available peg Accumulator indexes.
 */
export const getAvailableAccumIndex = (pegAccumulators: PegAccumulators) => {
  return pegAccumulators?.map(({pegAccumulator}) => pegAccumulator.index);
};

/**
 * Concate the pegAccumulator index and peglabel title
 */
export const pegLabelAccumIndexes = (pegAccumulators: PegAccumulators) => {
  return pegAccumulators?.map(({pegAccumulator}) => {
    return pegAccumulator.pegLabel.title.concat(pegAccumulator.index);
  });
};

/**
 * Check the duplication with available peg accumulators.
 */
export const checkDuplicateAccumulator = (pegAccumulators: PegAccumulators) => {
  const items       = [];
  let isDuplicateAccumulator = false;
  pegAccumulators?.forEach((item) => {
    const {index, pegLabel} = item.pegAccumulator;
    items.push(pegLabel?.title?.concat(index));
  });
  if (pegAccumulators.length > 0 && items.length > 0) {
    isDuplicateAccumulator = uniq(items).length !== pegAccumulators.length;
  }
  return isDuplicateAccumulator;
};

/**
 * Accumulator Format static data to show it in the drop down list.
 */
export const getAccumulatorFormats = () => {
  const newPath = `${path}.accumulatorFormat`;
  return [{
    value : translate(`${newPath}.signed.value`),
    label : translate(`${newPath}.signed.label`)
  }, {
    value : translate(`${newPath}.unSigned.value`),
    label : translate(`${newPath}.unSigned.label`)
  }];
};

/**
 * Get getSystemUnitOptions
 */
export const getSystemUnitOptions = (systemOfunits: systemOfUnits) => {
  return systemOfunits?.map((item) => {
    item.label = `${item?.name} [${item?.id}]`;
    item.value = item?.id;
    return item;
  });
};

/**
 * Regex for Conversion factor validation. While Add/Edit peg behavior window
 * the accumulator tab conversion factor value supporting below values from UI.
 * .0625,
 * /100
 * *1
 * *1.0
 * *0.1
 * *0.01
 * *.05
 */
export const validateFactor = (value: number | string): boolean => {
  const reg = /^[./*+-][\./]?(\d+(\.\d+)?)*$/;
  return reg.test(value);
};

/**
 * Check entire peg accumulator and verify
 * the conversion factor is valid or not.
 */
export const checkAllAccumulatorHasValidCFactor = (pegAccumulators: PegAccumulators) => {
  return pegAccumulators.find(({pegAccumulator}) => !pegAccumulator.isValidCFactor);
};

/**
   * Get PegLabel and generate the default peg label here
   * for Events and Accumulators.
   */
export const getCurrentPEGLabel = (pegAccumLabels: PegLabels, items: PegEvents | PegAccumulators) => {
  const {_self, name = null} = pegAccumLabels?.[items.length] || {};
  const {href, title = name, rel = 'pegLabel'} = _self || {};
  return {href, rel, title};
};

/**
 * Sorting the Event and Accumulators
 */
export const sortEventAccumulators = (item, type = 'pegAccumulator') => {
  const key = type === 'pegAccumulator' ? 'index' : 'eventCode';
  item.sort((
    a: { [x: string]: { [x: string]: number; }; },
    b: { [x: string]: { [x: string]: number; }; }
  ): number => {
    if (a[type][key] < b[type][key]) {
      return -1;
    }
    if (a[type][key] > b[type][key]) {
      return 1;
    }
    return 0;
  });
  return item;
};

/**
 * Concat all the peg Accumulators and return it as single array.
 */
export const concatPegAccumulators = (pegAccumulators: PegAccumulators): PegAccumulators => {
  return [].concat(...pegAccumulators) || [];
};

/**
 * Retrieve all the PEG Accumulators from available Events and return it.
 */
export const getAccumulators = ({pegEvents}: PegBehavior = {}): PegEvents => {
  return pegEvents?.map(({pegEvent:{pegAccumulators}}) => pegAccumulators) || [];
};

/**
 * Get Unique PEG Accumulators based on the unique peg label and SOU Qty values.
 */
export const getUniqueAccumulators = (pegAccumulators: PegAccumulators): PegAccumulators => {
  const results = [];
  pegAccumulators?.forEach(function(item) {
    const link           = item.pegAccumulator?.pegLabel.href;
    const cFactor        = item.pegAccumulator?.conversionFactor;
    const isValidCFactor = cFactor ? validateFactor(cFactor) : true;
    item.pegAccumulator.isValidCFactor = isValidCFactor;
    const index    = results.findIndex(({pegAccumulator: {pegLabel, index}}) => (
      pegLabel.href === link && index === item.pegAccumulator.index
    ));
    if (index <= -1) {
      results.push(item);
    }
  });
  return results;
};

/**
 * Manage the scroller to place it in the bottom of the row
 * while adding Events and Accumulators.
 */
export const controlScroll = (id: string, btnClicked: boolean): HTMLElement | any => {
  const element = document.querySelector(id);
  if (element !== null && btnClicked) {
    element.scrollTop = element.scrollHeight;
  }
  return element;
};

/**
 * Get unique Peg Accumulators.
 */
export const uniqueAccumulators = (pegAccumulators: PegAccumulators): PegAccumulators => {
  const accumulators = Array.from(new Set(
    pegAccumulators?.map((a: { pegAccumulator: { index: number; }; }) => a))
  );
  return accumulators;
};

/**
 * Check the accumulator index is duplicate from the
 * available Peg Accumulators.
 */
export const isDuplicateAccumulatorIndex = (pegAccumulators: PegAccumulators, currentAccumIndex: number): boolean => {
  return pegAccumulators?.filter(({pegAccumulator}) => pegAccumulator.index === currentAccumIndex).length > 1;
};

/**
 * Get unique PegLabels.
 */
export const uniquePegLabels = (pegLabels: PegLabels): PegLabels => {
  return Array.from(new Set(pegLabels.map(item => safeJSONStringify(item)))).map(item => safeJSONParse(item));
};

/**
 * Check duplicate Accumulators available in the accumulator list
 * and return boolean value.
 */
export const hasDuplicateAccumulator = (accumulators: PegAccumulators): boolean => {
  const uniqAccumulators = Array.from(new Set(accumulators?.map?.(item => item?.pegAccumulator?.index)));
  return uniqAccumulators?.length !== accumulators?.length;
};

/**
 * getNormalizePEGEvents method is used to normalize the pegAccumulators inside pegEvents.
 */
export const getNormalizePEGEvents = (pegEvents: PegEvents): PegEvents => {
  const normalPEGEvents = [];
  pegEvents?.map((item: PegEvent) => {
    const {pegAccumulators, id}   = item.pegEvent;
    item.pegEvent.pegAccumulators = normalizePegAccumulator(pegAccumulators, id, true);
    normalPEGEvents.push({...item});
  });
  return normalPEGEvents;
};

/**
 * Get PEG Events without Accumulators value..
 */
const getPEGEventWithoutAccumulator = (pegEvents: PegEvents): PegEvents => {
  return pegEvents?.map((item) => {
    const {pegAccumulators, ...rest} = item?.pegEvent;
    return {pegEvent: {...rest}};
  }) || [];
};

/**
 * Check if the pegBehavior's form data has changed and return boolean value.
 *
 * getPEGEventWithoutAccumulator method is used to get PEG Events without associated Accumulators
 * and check the changes of PEG Events only.
 * We are checking the changes of Accumulators associated with PEG Events in PegBehaviorConfigurationGrid
 * component...
 */
export function checkIsDataChanged(initialState, formState, isEdit = false): boolean {
  const {
    name,
    description,
    inherited,
    initialAccumCounts,
    pegEvents,
    accumulators
  } = initialState;
  const initialPegEvents     = getPEGEventWithoutAccumulator(pegEvents);
  const clonePegEvents       = cloneDeep(formState?.pegEvents?.value);
  const formPegEvents        = getPEGEventWithoutAccumulator(clonePegEvents);
  const initialAccumulators  = normalizePegAccumulator(accumulators, null, true);
  const clonePegAccumulators = cloneDeep(formState?.pegAccumulators?.value);
  const formAccumulators     = normalizePegAccumulator(clonePegAccumulators, null, true);
  const initialInherited     = inherited === undefined ? false : inherited;
  let unsavedItems           = false;

  switch (true) {
    case (undefinedAndNullToString(name) !== undefinedAndNullToString(formState?.name?.value)) :
      unsavedItems = true;
      break;
    case (undefinedAndNullToString(description) !== undefinedAndNullToString(formState?.description?.value)) :
      unsavedItems = true;
      break;
    case (!isEdit && Number(initialAccumCounts) !== Number(formState?.accumCounts?.value)) :
      unsavedItems = true;
      break;
    case (initialInherited !== formState?.inherited?.value) :
      unsavedItems = true;
      break;
    case (isEdit ? !isEqual(initialPegEvents, formPegEvents) : !isEmpty(formState?.pegEvents?.value)) :
      unsavedItems = true;
      break;
    case (isEdit ? !isEqual(initialAccumulators, formAccumulators)
      : !isEmpty(formState?.pegAccumulators?.value)) :
      unsavedItems = true;
      break;
    default :
      unsavedItems = false;
  };
  return unsavedItems;
};

/**
 * Check search value is availeble in PEG Label options or not.
 */
export const isSearchedOptionAvailable = (
  pegLabels: PegLabels,
  searchValue: String,
  TYPE = PegLabelType.EVENT
): boolean => {
  const pegEvents = getPEGLabels(pegLabels, TYPE);
  const isAvailable = pegEvents?.filter(({name}) =>
    name?.toLocaleLowerCase()?.includes(searchValue?.toLocaleLowerCase())) || [];
  return !!isAvailable?.length;
};

/**
 * Check if errors object have any true value or not ..
 */
export const isErrorMsg = (errors: IsErrorMsgParams): boolean => {
  if (errors && isObject(errors)) {
    return Object.values(errors)?.includes(true);
  }
  return false;
};

/**
 * Return true or false value on the basis of currentLabels length and loaded PegLabels length ...
 */
export const isLoadNextPageRecords = (currentLabels: number, totalLabels: number): boolean => {
  return currentLabels > totalLabels || totalLabels - currentLabels === 1;
};

/**
 * Check if given type is present in pegLabels or not.
 * If present it return true value otherwise return false.
 */
export const isPegLabelForGivenType = (pegLabels: PegLabels, type: PegLabelType): boolean => (
  pegLabels?.some(pegLabel => pegLabel?.type === type) || false
);

/**
 * Check if Event and Accumulator both type of data is present in pegLabels array.
 * Return true if present otherwise return a false value.
 */
export const hasEventAccumLabelsExists = (
  pegLabels: PegLabels,
  types = {event: PegLabelType.EVENT, accumulator: PegLabelType.ACCUMULATOR}
): boolean => {
  const {event, accumulator} = types;
  const isEvent              = isPegLabelForGivenType(pegLabels, event);
  const isAccumulator        = isPegLabelForGivenType(pegLabels, accumulator);
  return (isEvent && isAccumulator);
};

/**
 * Get Accumulators from PEG Behaviors object
 */
export const getInitialAccumulators = (pegBehaviors) => {
  const accumulators     = concatPegAccumulators(getAccumulators(pegBehaviors));
  const uniqAccumulators = getUniqueAccumulators(accumulators);
  return cloneDeep(sortEventAccumulators(uniqAccumulators));
};

/**
 * Get Accumulators by unique Accumulators IDs.
 */
export const getAccumulatorsByUniqId = (pegAccumulators: PegAccumulators): PegAccumulators => {
  const accumulators = Array.from(new Set(
    pegAccumulators?.map((a: {pegAccumulator: {id: number; }; }) => a?.pegAccumulator?.id))
  )?.map((id) => {
    return pegAccumulators?.find((a) => a?.pegAccumulator?.id === id);
  });
  return accumulators;
};

/**
 * Update the Peg Label, SOU Quantity, Unit, Conversion Factor and Accumulator Format value
 * inside PegEvent's pegAccumulator.
 */
export const getUpdatePegAccumulator = ({
  pegAccumulator,
  fieldName,
  souQuantity,
  units,
  pegLabel,
  accumulatorFormat,
  conversionFactor
}: getUpdatePegAccumulatorParams) => {
  switch (fieldName) {
    case AccumFieldsName.SOU_QUANTITY:
      pegAccumulator.souQuantity = souQuantity;
      pegAccumulator.units = units;
      break;
    case AccumFieldsName.PEG_LABEL:
      pegAccumulator.pegLabel = pegLabel;
      break;
    case AccumFieldsName.ACCUMULATOR_FORMAT:
      pegAccumulator.accumulatorFormat = accumulatorFormat;
      break;
    case AccumFieldsName.CONVERSION_FACTOR:
      pegAccumulator.conversionFactor = conversionFactor;
      break;
  };
  return pegAccumulator;
};
